import React, { useEffect, useState } from "react";
import { Box, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CommunityContent from "../../Components/Community/CommunityContent";
import Layout from "../../layouts/Layout";
import SubscribeVideoDrawer from "../../Components/Common/SubscribeVideoDrawer";
import { ConfirmationSnackbar } from "../../Components/Common/CustomSnackbar";
import axiosInstance from "../../utils/config/config";
import { CustomBtn } from "../../Components/Common/CustomBtn";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Community = () => {
  const [searchTerm, setSearchTerm] = useState(""); // Actual search term for API
  const [tempSearch, setTempSearch] = useState(""); // Temporary input field state
  const [pageNo, setPageNo] = useState(1);
  const [recommendations, setRecommendations] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch recommendations (Runs when pageNo or searchTerm changes)
  useEffect(() => {
    if (pageNo > 1) {
      getRecommendations(false, searchTerm); // Append results on load more
    }
  }, [pageNo]);

  useEffect(() => {
    getRecommendations(true, searchTerm); // First time fetch
  }, []);

  // ✅ API Call to fetch recommendations
  const getRecommendations = async (reset = false, searchQuery) => {
    if (loading) return; // Prevent duplicate calls

    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/post/getRecommendations?search=${encodeURIComponent(
          searchQuery
        )}&page=${pageNo}&limit=10`
      );

      setTotalPages(data.totalPages);

      if (reset) {
        setRecommendations(data.recommendations); // ✅ Replace results on new search
      } else {
        setRecommendations((prev) => [...prev, ...data.recommendations]); // ✅ Append for pagination
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle input change without hitting API
  const handleSearchInput = (event) => {
    setTempSearch(event.target.value); // Update only temporary state
  };

  // ✅ Trigger search when "Search" button is clicked
  const handleSearchClick = () => {
    setSearchTerm(tempSearch); // ✅ Update searchTerm state
    setPageNo(1); // ✅ Reset to page 1
    setRecommendations([]); // ✅ Clear previous results immediately
    getRecommendations(true, tempSearch); // ✅ Pass tempSearch directly to API
  };

  // ✅ Load more recommendations
  const loadMore = () => {
    if (pageNo < totalPages) {
      setPageNo((prevPage) => prevPage + 1);
    }
  };

  return (
    <Elements stripe={stripePromise}>
      <Layout>
        {/* Search Bar */}

        <Box
          sx={{
            mb: 2,
            mt: 2,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "nowrap",
            gap: 1,
            maxWidth: "100%",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search..."
            value={tempSearch} // Controlled input
            onChange={handleSearchInput} // Updates tempSearch only
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#FC0108" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "0px",
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "0px",
                borderColor: "#000",
                "&:hover fieldset": { borderColor: "#FC0108" },
                "&.Mui-focused fieldset": { borderColor: "#FC0108" },
              },
            }}
          />

          {/* Search Button */}
          <CustomBtn
            text="Search"
            noIcon
            width="100px"
            height="45px"
            sx={{
              minWidth: "auto",
              maxWidth: "100px",
              backgroundColor: "#FC0108",
              "&:hover": { backgroundColor: "#D00000" },
            }}
            onClick={handleSearchClick}
          />
        </Box>

        {/* Community Content */}
        <CommunityContent
          allData={recommendations || []}
          type={"all"}
          loadMore={loadMore}
          refreshFunction={getRecommendations}
          pageNo={pageNo}
          queryParam={searchTerm}
          loadMoreDisabled={pageNo >= totalPages || loading}
          setDrawerState={() => {}}
          dontShowActions
          setCreatorData={() => {}}
          setPaidVideoData={() => {}}
        />

        {/* Subscribe Drawer */}
        <SubscribeVideoDrawer drawerState={false} setDrawerState={() => {}} />

        {/* Snackbar */}
        <ConfirmationSnackbar
          snackbarState={false}
          handleSnackbarClose={() => {}}
          text={"You have subscribed to this creator successfully!"}
        />
      </Layout>
    </Elements>
  );
};

export default Community;
